/**
 * Static dropdown sources for the address picker.
 *
 * Pincode lookups (`pincodeLookup.ts`) are the primary way users fill
 * out city + state — it auto-completes from a 6-digit ZIP. These
 * lists are the *fallback* used when the pincode service can't be
 * reached or when the user is editing a country other than India.
 *
 * Kept compact on purpose; the panel has no analytics need for the
 * full 5-digit-village-resolution gazetteer.
 */

import type { SearchableSelectOption } from '@kss/ui';

/**
 * State options use the **GST state code** (Indian standard, 2-digit
 * numeric — same first two digits of any GSTIN) as `value` and the
 * state name as `label`. Pairs naturally with the GST number on the
 * same form (Maharashtra GSTIN starts `27…`, value here is `27`).
 *
 * Reference: https://www.gst.gov.in/ — list of state codes.
 */
export const INDIAN_STATES: SearchableSelectOption[] = [
  { value: '01', label: 'Jammu & Kashmir', description: 'UT · 01' },
  { value: '02', label: 'Himachal Pradesh', description: '02' },
  { value: '03', label: 'Punjab', description: '03' },
  { value: '04', label: 'Chandigarh', description: 'UT · 04' },
  { value: '05', label: 'Uttarakhand', description: '05' },
  { value: '06', label: 'Haryana', description: '06' },
  { value: '07', label: 'Delhi', description: 'UT · 07' },
  { value: '08', label: 'Rajasthan', description: '08' },
  { value: '09', label: 'Uttar Pradesh', description: '09' },
  { value: '10', label: 'Bihar', description: '10' },
  { value: '11', label: 'Sikkim', description: '11' },
  { value: '12', label: 'Arunachal Pradesh', description: '12' },
  { value: '13', label: 'Nagaland', description: '13' },
  { value: '14', label: 'Manipur', description: '14' },
  { value: '15', label: 'Mizoram', description: '15' },
  { value: '16', label: 'Tripura', description: '16' },
  { value: '17', label: 'Meghalaya', description: '17' },
  { value: '18', label: 'Assam', description: '18' },
  { value: '19', label: 'West Bengal', description: '19' },
  { value: '20', label: 'Jharkhand', description: '20' },
  { value: '21', label: 'Odisha', description: '21' },
  { value: '22', label: 'Chhattisgarh', description: '22' },
  { value: '23', label: 'Madhya Pradesh', description: '23' },
  { value: '24', label: 'Gujarat', description: '24' },
  { value: '26', label: 'Dadra & Nagar Haveli and Daman & Diu', description: 'UT · 26' },
  { value: '27', label: 'Maharashtra', description: '27' },
  { value: '29', label: 'Karnataka', description: '29' },
  { value: '30', label: 'Goa', description: '30' },
  { value: '31', label: 'Lakshadweep', description: 'UT · 31' },
  { value: '32', label: 'Kerala', description: '32' },
  { value: '33', label: 'Tamil Nadu', description: '33' },
  { value: '34', label: 'Puducherry', description: 'UT · 34' },
  { value: '35', label: 'Andaman & Nicobar Islands', description: 'UT · 35' },
  { value: '36', label: 'Telangana', description: '36' },
  { value: '37', label: 'Andhra Pradesh', description: '37' },
  { value: '38', label: 'Ladakh', description: 'UT · 38' },
];

/**
 * Top metro / Tier-1 / Tier-2 Indian cities. Searchable; users can
 * also type a city not on this list (the SearchableSelect's
 * `allowCustom` flag is on for these fields).
 */
export const INDIAN_CITIES: SearchableSelectOption[] = [
  'Ahmedabad',
  'Bengaluru',
  'Bhopal',
  'Bhubaneswar',
  'Chandigarh',
  'Chennai',
  'Coimbatore',
  'Dehradun',
  'Delhi',
  'Faridabad',
  'Ghaziabad',
  'Goa',
  'Gurugram',
  'Guwahati',
  'Hyderabad',
  'Indore',
  'Jaipur',
  'Jamshedpur',
  'Kanpur',
  'Kochi',
  'Kolkata',
  'Lucknow',
  'Ludhiana',
  'Madurai',
  'Mumbai',
  'Mysuru',
  'Nagpur',
  'Nashik',
  'New Delhi',
  'Noida',
  'Patna',
  'Pune',
  'Raipur',
  'Ranchi',
  'Surat',
  'Thane',
  'Thiruvananthapuram',
  'Vadodara',
  'Varanasi',
  'Vijayawada',
  'Visakhapatnam',
].map((c) => ({ value: c, label: c }));

/**
 * Country options use the ITU-T E.164 international calling code
 * (e.g. `91` for India, `1` for US/Canada, `44` for UK) as `value`
 * and the human-readable name as `label`.
 *
 * Why ISD codes vs ISO alpha-2:
 *   - The live API has `country varchar(5)` — both fit, but ISD
 *     codes round-trip naturally with phone numbers ("+91…") so the
 *     saved value carries semantic information.
 *   - Standardised by ITU-T (Recommendation E.164) — the same
 *     identifier you'd use for any phone-aware integration.
 *
 * `description` shows the alpha-2 ISO code as a hint so power users
 * still see both. Once the backend widens `country` to `varchar(64)`
 * we can swap to full names transparently (the trigger renders the
 * label either way).
 */
export const COUNTRIES: SearchableSelectOption[] = [
  // India sits at the top because the live tenant base is India-first.
  { value: '91', label: 'India', description: 'IN · +91' },
  { value: '61', label: 'Australia', description: 'AU · +61' },
  { value: '880', label: 'Bangladesh', description: 'BD · +880' },
  { value: '975', label: 'Bhutan', description: 'BT · +975' },
  { value: '86', label: 'China', description: 'CN · +86' },
  { value: '49', label: 'Germany', description: 'DE · +49' },
  { value: '62', label: 'Indonesia', description: 'ID · +62' },
  { value: '81', label: 'Japan', description: 'JP · +81' },
  { value: '60', label: 'Malaysia', description: 'MY · +60' },
  { value: '960', label: 'Maldives', description: 'MV · +960' },
  { value: '977', label: 'Nepal', description: 'NP · +977' },
  { value: '92', label: 'Pakistan', description: 'PK · +92' },
  { value: '966', label: 'Saudi Arabia', description: 'SA · +966' },
  { value: '65', label: 'Singapore', description: 'SG · +65' },
  { value: '94', label: 'Sri Lanka', description: 'LK · +94' },
  { value: '66', label: 'Thailand', description: 'TH · +66' },
  { value: '971', label: 'United Arab Emirates', description: 'AE · +971' },
  { value: '44', label: 'United Kingdom', description: 'GB · +44' },
  // North American Numbering Plan: USA + Canada share the +1 prefix.
  { value: '1', label: 'United States / Canada', description: 'US · CA · +1' },
];

/**
 * Resolve the human-readable label for a stored state / country
 * value. The form persists machine-friendly codes (GST 2-digit
 * state code, ITU-T country calling code), but the Review &
 * Save tab — and any other read-only display — should show the
 * actual name. Unknown codes fall through to the raw value so the
 * user sees *something* even for custom-typed entries.
 *
 *   labelForState('27')          → 'Maharashtra'
 *   labelForState('Karnataka')   → 'Karnataka'   (already a name)
 *   labelForCountry('91')        → 'India'
 *   labelForCountry('1')         → 'United States / Canada'
 */
export function labelForState(value: string | null | undefined): string {
  const v = (value ?? '').trim();
  if (!v) return '';
  const hit = INDIAN_STATES.find((o) => o.value === v);
  return hit?.label ?? v;
}

export function labelForCountry(value: string | null | undefined): string {
  const v = (value ?? '').trim();
  if (!v) return '';
  const hit = COUNTRIES.find((o) => o.value === v);
  return hit?.label ?? v;
}
